import React, { useRef, forwardRef } from 'react';

import { motion, useInView, useReducedMotion } from 'framer-motion';
import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';

import { StyledLinearProgress } from './AnimationProgress.style';

const MotionLinearProgress = motion.create(StyledLinearProgress);

/**
 * AnimationProgress component displays an animated linear progress bar that animates when it comes into view
 * @param {object} props - Component props
 * @param {number} props.value - Progress value (0-100)
 * @param {string} props.variant - Progress variant (determinate, indeterminate)
 * @param {number} props.duration - Animation duration in seconds
 * @param {number} props.delay - Animation delay in seconds
 * @param {string} props.ariaLabel - Accessibility label for the progress bar
 * @param {number} props.height - Height of the progress bar in pixels
 * @param {object} props.transition - Custom transition configuration
 * @returns {React.ReactElement} The AnimationProgress component
 */
const AnimationProgress = forwardRef(function AnimationProgress(
  {
    value = 100,
    variant = 'determinate',
    duration = 0.6,
    delay = 0,
    ariaLabel = 'Loading progress',
    height = 2,
    transition: customTransition,
    ...restProps
  },
  ref
) {
  // Hooks must be called at the top level, not inside try-catch
  const internalRef = useRef(null);
  const finalRef = ref || internalRef;
  const isInView = useInView(finalRef, { once: true });
  const prefersReducedMotion = useReducedMotion();

  // Input validation and error handling
  try {
    // Validate value range
    const clampedValue = Math.max(0, Math.min(100, value || 0));

    // Validate duration and delay
    const safeDuration = Math.max(0.1, duration || 0.6);
    const safeDelay = Math.max(0, delay || 0);

    // Validate height
    const safeHeight = Math.max(1, height || 2);

    const defaultTransition = prefersReducedMotion
      ? { duration: 0, delay: 0, ease: 'linear' }
      : {
          duration: safeDuration,
          delay: safeDelay,
          ease: 'easeOut'
        };

    const transition = customTransition || defaultTransition;

    return (
      <Box>
        <MotionLinearProgress
          ref={finalRef}
          animate={{ width: isInView || prefersReducedMotion ? '100%' : 0 }}
          aria-label={ariaLabel}
          aria-valuemax={variant === 'determinate' ? 100 : undefined}
          aria-valuemin={variant === 'determinate' ? 0 : undefined}
          aria-valuenow={variant === 'determinate' ? clampedValue : undefined}
          height={safeHeight}
          initial={{ width: prefersReducedMotion ? '100%' : 0 }}
          role="progressbar"
          transition={transition}
          value={clampedValue}
          variant={variant}
          {...restProps}
        />
      </Box>
    );
  } catch (error) {
    // Error fallback - render a simple progress bar without animation
    console.error('AnimationProgress component error:', error);

    return (
      <Box>
        <StyledLinearProgress
          ref={finalRef}
          aria-label={ariaLabel || 'Loading progress'}
          height={Math.max(1, height || 2)}
          role="progressbar"
          value={Math.max(0, Math.min(100, value || 0))}
          variant={variant}
        />
      </Box>
    );
  }
});

AnimationProgress.propTypes = {
  value: PropTypes.number,
  variant: PropTypes.oneOf(['determinate', 'indeterminate']),
  duration: PropTypes.number,
  delay: PropTypes.number,
  ariaLabel: PropTypes.string,
  height: PropTypes.number,
  transition: PropTypes.object
};

export default AnimationProgress;
